import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Sparkles, FileText } from 'lucide-react';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#7c3aed', '#2563eb', '#06b6d4', '#d946ef', '#f43f5e'];

const SharedDashboardPage = () => {
  const { token } = useParams();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiAnalysis, setAiAnalysis] = useState(null);

  useEffect(() => {
    loadDashboard();
  }, [token]);

  const loadDashboard = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/share/${token}`);
      setDashboard(response.data);
      setAiAnalysis(JSON.parse(response.data.ai_analysis));
    } catch (error) {
      toast.error('Dashboard not found or not public');
    } finally {
      setLoading(false);
    }
  };

  const renderChart = (chart, index) => {
    const commonProps = {
      data: chart.data,
      margin: { top: 5, right: 30, left: 20, bottom: 5 }
    };

    switch (chart.type) {
      case 'bar':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart {...commonProps}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
              <XAxis dataKey={chart.xKey} stroke="#a1a1aa" />
              <YAxis stroke="#a1a1aa" />
              <Tooltip contentStyle={{ background: '#18181b', border: '1px solid rgba(255,255,255,0.1)' }} />
              <Legend />
              <Bar dataKey={chart.yKey} fill="#7c3aed" />
            </BarChart>
          </ResponsiveContainer>
        );
      
      case 'line':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart {...commonProps}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
              <XAxis dataKey={chart.xKey} stroke="#a1a1aa" />
              <YAxis stroke="#a1a1aa" />
              <Tooltip contentStyle={{ background: '#18181b', border: '1px solid rgba(255,255,255,0.1)' }} />
              <Legend />
              <Line type="monotone" dataKey={chart.yKey} stroke="#2563eb" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        );
      
      case 'area':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart {...commonProps}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
              <XAxis dataKey={chart.xKey} stroke="#a1a1aa" />
              <YAxis stroke="#a1a1aa" />
              <Tooltip contentStyle={{ background: '#18181b', border: '1px solid rgba(255,255,255,0.1)' }} />
              <Legend />
              <Area type="monotone" dataKey={chart.yKey} stroke="#06b6d4" fill="#06b6d4" fillOpacity={0.3} />
            </AreaChart>
          </ResponsiveContainer>
        );
      
      case 'pie':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={chart.data}
                dataKey={chart.dataKey}
                nameKey={chart.nameKey}
                cx="50%"
                cy="50%"
                outerRadius={100}
                label
              >
                {chart.data.map((entry, i) => (
                  <Cell key={`cell-${i}`} fill={COLORS[i % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip contentStyle={{ background: '#18181b', border: '1px solid rgba(255,255,255,0.1)' }} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        );
      
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-xl text-muted-foreground">Loading shared dashboard...</div>
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center">
        <div className="text-2xl font-bold mb-4">Dashboard Not Found</div>
        <p className="text-muted-foreground mb-8">This dashboard may have been removed or the link is invalid.</p>
        <Link to="/">
          <Button>Go to Home</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="glass border-b border-white/5 h-20 flex items-center justify-between px-8 sticky top-0 z-50">
        <div className="flex items-center gap-2">
          <Sparkles className="h-6 w-6 text-primary" />
          <span className="text-xl font-bold">{dashboard.file_name}</span>
        </div>
        <Link to="/">
          <Button variant="outline" size="sm" data-testid="home-btn" className="rounded-full">
            Create Your Own Dashboard
          </Button>
        </Link>
      </header>

      <div className="container mx-auto px-8 py-12 max-w-7xl">
        <div className="mb-8 text-center">
          <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-full px-4 py-2 text-sm text-primary font-medium">
            <span>Shared Dashboard</span>
          </div>
        </div>

        {aiAnalysis && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-8"
          >
            {/* Executive Summary */}
            <div className="glass rounded-2xl p-8" data-testid="shared-executive-summary">
              <h2 className="text-2xl font-bold mb-4 text-primary">Executive Summary</h2>
              <p className="text-lg text-muted-foreground leading-relaxed">{aiAnalysis.executive_summary}</p>
            </div>

            {/* Charts */}
            {dashboard.chart_data && dashboard.chart_data.length > 0 && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold">Visualizations</h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {dashboard.chart_data.map((chart, index) => (
                    <div key={index} className="glass rounded-2xl p-6" data-testid={`shared-chart-${index}`}>
                      <h3 className="text-lg font-semibold mb-4">{chart.title}</h3>
                      {renderChart(chart, index)}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Detailed Insights */}
            {aiAnalysis.detailed_insights && aiAnalysis.detailed_insights.length > 0 && (
              <div className="glass rounded-2xl p-8" data-testid="shared-key-insights">
                <h2 className="text-2xl font-bold mb-4 text-accent">Detailed Insights</h2>
                <div className="space-y-4">
                  {aiAnalysis.detailed_insights.map((insight, index) => (
                    <div key={index} className="border-l-4 border-accent pl-4">
                      <div className="text-xs text-chart-3 uppercase tracking-wider mb-1">{insight.category}</div>
                      <h3 className="text-lg font-semibold mb-2">{insight.title}</h3>
                      <p className="text-muted-foreground leading-relaxed mb-2">{insight.description}</p>
                      <p className="text-primary italic text-sm"><strong>Impact:</strong> {insight.impact}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Statistical Analysis */}
            {aiAnalysis.statistical_analysis && aiAnalysis.statistical_analysis.length > 0 && (
              <div className="glass rounded-2xl p-8">
                <h2 className="text-2xl font-bold mb-4 text-chart-3">Statistical Analysis</h2>
                <div className="space-y-3">
                  {aiAnalysis.statistical_analysis.map((stat, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <span className="text-chart-3 mt-1">•</span>
                      <div>
                        <strong className="text-foreground">{stat.metric}:</strong>{' '}
                        <span className="text-muted-foreground leading-relaxed">{stat.finding}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recommendations */}
            {aiAnalysis.recommendations && aiAnalysis.recommendations.length > 0 && (
              <div className="glass rounded-2xl p-8">
                <h2 className="text-2xl font-bold mb-4 text-chart-4">Actionable Recommendations</h2>
                <div className="space-y-4">
                  {aiAnalysis.recommendations.map((rec, index) => {
                    const priorityColors = {
                      'High': 'border-red-500 bg-red-500/10',
                      'Medium': 'border-yellow-500 bg-yellow-500/10',
                      'Low': 'border-green-500 bg-green-500/10'
                    };
                    const priorityColor = priorityColors[rec.priority] || priorityColors['Medium'];
                    
                    return (
                      <div key={index} className={`border-l-4 ${priorityColor} pl-4 py-3 rounded-r-lg`}>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs font-bold uppercase px-2 py-1 rounded bg-chart-4/20 text-chart-4">
                            {rec.priority} Priority
                          </span>
                          <span className="text-xs font-medium text-accent uppercase">{rec.category}</span>
                        </div>
                        <p className="text-foreground font-medium mb-2">{rec.recommendation}</p>
                        <p className="text-sm text-muted-foreground italic">
                          <strong>Expected Impact:</strong> {rec.expected_impact}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-12 glass rounded-2xl p-8 text-center"
        >
          <h3 className="text-2xl font-bold mb-4">Want to create your own AI-powered dashboards?</h3>
          <p className="text-muted-foreground mb-6">Start with 3 free dashboards, no credit card required</p>
          <Link to="/register">
            <Button size="lg" className="rounded-full px-8 py-6" data-testid="shared-cta-btn">
              <Sparkles className="h-5 w-5 mr-2" /> Get Started Free
            </Button>
          </Link>
        </motion.div>
      </div>
    </div>
  );
};

export default SharedDashboardPage;